<template>
  <div class="historico-card">
    <expandable-filters :open.sync="showFilters">
      <template #leading>
        <div class="titulo-area">
          <h3>Histórico Recente</h3>
          <p>{{ subtitle }}</p>
        </div>
      </template>

      <template #actions>
        <button
          class="btn-pdf"
          :disabled="isGeneratingPdf || registrosSelecionados.length === 0"
          @click="gerarEBaixarPdf"
        >
          <span v-if="isGeneratingPdf">
            Gerando PDF...
          </span>
          <span v-else>
            Baixar PDF ({{ registrosSelecionados.length }})
          </span>
        </button>
      </template>

      <select
        class="select-filtro"
        :value="selectedEquipmentId == null ? '' : selectedEquipmentId"
        @change="onEquipmentChange"
      >
        <option value="">Todas Máquinas</option>
        <option
          v-for="equipment in equipments"
          :key="equipment.id"
          :value="equipment.id"
        >
          {{ equipment.identification }}
        </option>
      </select>
      <div class="periodo-filtro">
        <date-range-picker
          :start-date.sync="dateRangeStart"
          :end-date.sync="dateRangeEnd"
          placeholder="Selecione o período"
        />
      </div>
      <button class="btn-limpar" type="button" @click="limparFiltros">
        Limpar filtros
      </button>
    </expandable-filters>

    <div v-if="errorMessage" class="error-banner">
      <span>{{ errorMessage }}</span>
      <button class="btn-fechar-erro" @click="errorMessage = ''">&times;</button>
    </div>

    <div v-if="isLoading" class="estado-vazio">
      <p>Carregando histórico...</p>
    </div>

    <div v-else-if="registros.length === 0" class="estado-vazio">
      <p>{{ emptyMessage }}</p>
    </div>

    <table v-else class="tabela-historico">
      <thead>
        <tr>
          <th class="col-checkbox">
            <input
              type="checkbox"
              @change="toggleSelecionarTodos"
              :checked="registrosSelecionados.length === registros.length && registros.length > 0"
            />
          </th>
          <th class="col-operador">OPERADOR</th>
          <th class="col-codigo">CÓDIGO</th>
          <th class="col-duracao">DURAÇÃO</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="registro in registros" :key="registro.id">
          <td class="col-checkbox">
            <input
              type="checkbox"
              :value="registro.id"
              v-model="registrosSelecionados"
            />
          </td>
          <td class="col-operador font-bold">{{ registro.operador }}</td>
          <td class="col-codigo">{{ registro.sampleId }}</td>
          <td class="col-duracao font-bold">
            {{ registro.duracaoFormatada || formatarDuracao(registro.duracaoSegundos) }}
          </td>
        </tr>
      </tbody>
    </table>

    <b-pagination
      v-if="totalCount > pageSize"
      v-model="page"
      :total-rows="totalCount"
      :per-page="pageSize"
      align="center"
      size="sm"
      class="historico-pagination"
    />
  </div>
</template>

<script>
import { api } from '@/services'
import { DB_ROUTES } from '@/constants'
import ExpandableFilters from '@/components/static/ExpandableFilters.vue'

function toIsoDate (date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function todayIsoDate () {
  return toIsoDate(new Date())
}

function formatDisplayDate (iso) {
  if (!iso) return ''
  const [year, month, day] = String(iso).split('-')
  if (!year || !month || !day) return iso
  return `${day}/${month}/${year}`
}

export default {
  name: 'HistoricoRecente',
  components: {
    ExpandableFilters
  },
  props: {
    equipments: {
      type: Array,
      default: () => []
    },
    selectedEquipmentId: {
      type: [Number, String],
      default: null
    }
  },
  emits: ['update:selectedEquipmentId'],
  data () {
    return {
      isLoading: false,
      isGeneratingPdf: false,
      errorMessage: '',
      showFilters: false,
      dateRangeStart: todayIsoDate(),
      dateRangeEnd: todayIsoDate(),
      page: 1,
      pageSize: 5,
      totalCount: 0,
      registros: [],
      registrosSelecionados: []
    }
  },
  computed: {
    filterKey () {
      return `${this.dateRangeStart}|${this.dateRangeEnd}|${this.selectedEquipmentId}`
    },
    subtitle () {
      const { startDate, endDate } = this.getDateRange()
      if (startDate === endDate) {
        return startDate === todayIsoDate()
          ? 'Análises concluídas hoje'
          : `Análises de ${formatDisplayDate(startDate)}`
      }
      return `Análises de ${formatDisplayDate(startDate)} a ${formatDisplayDate(endDate)}`
    },
    emptyMessage () {
      const { startDate, endDate } = this.getDateRange()
      if (startDate === endDate) {
        return startDate === todayIsoDate()
          ? 'Nenhum registro encontrado hoje.'
          : `Nenhum registro encontrado em ${formatDisplayDate(startDate)}.`
      }
      return 'Nenhum registro encontrado no período selecionado.'
    }
  },
  watch: {
    filterKey () {
      if (this.page !== 1) {
        this.page = 1
        return
      }
      this.carregarHistorico()
    },
    page () {
      this.carregarHistorico()
    }
  },
  created () {
    this.carregarHistorico()
  },
  methods: {
    getDateRange () {
      const today = todayIsoDate()
      const startDate = this.dateRangeStart || today
      const endDate = this.dateRangeEnd || startDate
      return { startDate, endDate }
    },
    formatarDuracao (segundos) {
      if (!segundos && segundos !== 0) return '-'
      const total = Math.floor(Number(segundos))
      const minutos = Math.floor(total / 60)
      const segsRestantes = total % 60
      if (minutos === 0) return `${segsRestantes}s`
      if (segsRestantes === 0) return `${minutos} min`
      return `${minutos} min ${segsRestantes} s`
    },
    onEquipmentChange (event) {
      const value = event.target.value
      this.$emit('update:selectedEquipmentId', value === '' ? null : Number(value))
    },
    limparFiltros () {
      const today = todayIsoDate()
      const datesAlreadyDefault = this.dateRangeStart === today && this.dateRangeEnd === today
      this.dateRangeStart = today
      this.dateRangeEnd = today
      if (this.selectedEquipmentId != null) {
        this.$emit('update:selectedEquipmentId', null)
        return
      }
      if (datesAlreadyDefault) {
        this.carregarHistorico()
      }
    },
    toggleSelecionarTodos (evento) {
      if (evento.target.checked) {
        this.registrosSelecionados = this.registros.map(r => r.id)
      } else {
        this.registrosSelecionados = []
      }
    },
    async carregarHistorico () {
      this.isLoading = true
      this.errorMessage = ''
      this.registrosSelecionados = []

      try {
        const { startDate, endDate } = this.getDateRange()
        const response = await api.get(DB_ROUTES.GET_ANALYSIS_DASHBOARD_RECENT_HISTORY({
          equipmentId: this.selectedEquipmentId || undefined,
          startDate,
          endDate,
          pageNumber: this.page,
          pageSize: this.pageSize
        }))

        const data = response.data || {}
        const items = data.items || data.Items || []
        this.totalCount = Number(data.totalCount ?? data.TotalCount ?? items.length)
        this.registros = items.map(item => ({
          id: item.analysisId ?? item.AnalysisId,
          operador: item.operatorName || item.OperatorName || '—',
          sampleId: item.sampleId || item.SampleId || '—',
          duracaoSegundos: item.durationSeconds ?? item.DurationSeconds ?? 0,
          duracaoFormatada: item.durationFormatted || item.DurationFormatted || ''
        }))
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
        this.registros = []
        this.totalCount = 0
        this.errorMessage = error.response?.data?.message || 'Não foi possível carregar o histórico.'
      } finally {
        this.isLoading = false
      }
    },
    async gerarEBaixarPdf () {
      if (this.registrosSelecionados.length === 0) return

      this.isGeneratingPdf = true
      this.errorMessage = ''

      try {
        const { startDate, endDate } = this.getDateRange()
        const response = await api.post(
          DB_ROUTES.EXPORT_ANALYSIS_DASHBOARD_PDF,
          {
            equipmentId: this.selectedEquipmentId || undefined,
            startDate,
            endDate,
            analysisIds: this.registrosSelecionados
          },
          { responseType: 'blob' }
        )

        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        const disposition = response.headers['content-disposition'] || ''
        const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        const fileName = match
          ? match[1].replace(/['"]/g, '')
          : `historico_analises_${Date.now()}.pdf`

        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Erro ao exportar PDF:', error)
        let message = 'Ocorreu um erro ao gerar o relatório em PDF.'
        const data = error.response?.data
        if (data instanceof Blob) {
          try {
            const text = await data.text()
            const parsed = JSON.parse(text)
            message = parsed.message || parsed.title || message
          } catch (_) { /* keep default */ }
        } else if (error.response?.data?.message) {
          message = error.response.data.message
        }
        this.errorMessage = message
      } finally {
        this.isGeneratingPdf = false
      }
    }
  }
}
</script>

<style scoped>
.historico-card {
  background-color: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.05);
  font-family: 'Inter', sans-serif;
  overflow: visible;
}

.titulo-area h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333333;
}

.titulo-area p {
  margin: 4px 0 0 0;
  font-size: 12px;
  color: #888888;
}

.btn-pdf {
  background-color: #2c3e50;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
}

.btn-pdf:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Tratamento de Erro */
.error-banner {
  background-color: #FEE2E2;
  color: #991B1B;
  padding: 10px 14px;
  border-radius: 6px;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
}

.btn-fechar-erro {
  background: none;
  border: none;
  color: #991B1B;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
}

.estado-vazio {
  padding: 40px 0;
  text-align: center;
  color: #888888;
  font-size: 14px;
}

/* Tabela */
.tabela-historico {
  width: 100%;
  border-collapse: collapse;
}

.tabela-historico th {
  color: #FF8A65;
  font-size: 11px;
  font-weight: 700;
  padding-bottom: 16px;
  border-bottom: 1px solid #F0F0F0;
  text-align: left;
}

.tabela-historico td {
  padding: 16px 0;
  font-size: 13px;
  color: #555555;
  border-bottom: 1px solid #F9F9F9;
}

.col-checkbox {
  width: 40px;
}

.font-bold {
  font-weight: 600;
  color: #333333;
}

.historico-pagination {
  margin-top: 16px;
  margin-bottom: 0;
}

.historico-pagination ::v-deep .page-link {
  color: #FF5722;
}

.historico-pagination ::v-deep .page-item.active .page-link {
  background-color: #FF5722;
  border-color: #FF5722;
  color: #ffffff;
}
</style>
